import React, {
  Suspense,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import DialogModal from "src/common/DialogModal";
import ChartCard from "src/common/charts/ChartCard";
import CandleChart from "src/common/charts/candleChart";
import HpbChart from "src/common/charts/hpbChart";
import LineChart from "src/common/charts/lineChart";
import RsiLineChart from "src/common/charts/rsiLineChart";
import AppContext from "src/components/AppContext";
import ChartSkeleton from "src/components/skeleton/ChartSkeleton";
import {
  candleDetailsCardWrapperClass,
  getCandleDetailsTitle,
  renderCandleDetailsHtml,
  renderCandleDetailsReact,
} from "src/helpers";
import { getLocalStorageUser } from "src/helpers/api/sharedFunction";
import { useStockCharts } from "src/hooks/useStockCharts";

interface OrderBlockProps {
  symbol: string;
  obDate: string | undefined;
  retestDate: string | undefined;
}

type ChartType = "Line" | "Candle" | "Rsi" | "Hpb";

const PlaceholderMessage = React.memo(function PlaceholderMessage({
  message,
}: {
  message: string;
}) {
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-gray-500 dark:text-gray-400 text-center">
        {message}
      </div>
    </div>
  );
});

const OrderBlockChart = React.memo(function OrderBlockChart({
  symbol,
  retestDate,
  obDate,
}: OrderBlockProps) {
  const user = getLocalStorageUser();
  const token = user.token;
  const context = useContext(AppContext);
  const { darkMode } = context.state;
  const isDark = !!darkMode;

  const [toggleChart, setToggleChart] = useState<boolean>(false);
  const [expandChart, setExpandChart] = useState<ChartType>("Candle");
  const [expandChartTitle, setExpandChartTitle] = useState<string>("");
  const [showModal, setShowModal] = useState(false);

  const {
    chartData,
    rsiData,
    hpbData,
    hpbYear,
    loading: loadingCharts,
    setHoveredCandle,
    activeCandle,
  } = useStockCharts(symbol, token, false, false, false);

  const noSymbolSelected = !symbol || symbol === "";

  const renderChart = useCallback(
    (type: ChartType, fullSize = false) => {
      switch (type) {
        case "Line":
          return (
            <LineChart
              lineData={chartData}
              symbolName={symbol}
              darkMode={isDark}
              isOrderblock={true}
              isExpand={fullSize}
            />
          );
        case "Rsi":
          return (
            <RsiLineChart
              rsiData={rsiData}
              darkMode={isDark}
              isOrderblock={true}
              isExpand={fullSize}
            />
          );
        case "Hpb":
          return (
            <HpbChart
              hpbChart={hpbData}
              darkMode={isDark}
              isOrderblock={true}
              isExpand={fullSize}
            />
          );
        case "Candle":
          return (
            <CandleChart
              symbolName={symbol}
              candleData={chartData}
              darkMode={isDark}
              isExpand={fullSize}
              isOrderblock={true}
              obDate={obDate}
              isOrderBlock={true}
              retestDate={retestDate}
              onHoverChange={setHoveredCandle}
            />
          );
        default:
          return null;
      }
    },
    [
      chartData,
      rsiData,
      hpbData,
      isDark,
      obDate,
      retestDate,
      symbol,
      setHoveredCandle,
    ],
  );

  const handleExpandChart = useCallback(
    (chartType: ChartType, title: string) => {
      setExpandChart(chartType);
      setExpandChartTitle(title);
      setShowModal(true);
    },
    [],
  );

  const mainChartType: ChartType = toggleChart ? "Line" : "Candle";

  const onExpandMain = useMemo(() => {
    if (noSymbolSelected || loadingCharts || chartData.length === 0)
      return undefined;
    return () => handleExpandChart(mainChartType, "1 Year Chart (TF: 1 Day)");
  }, [
    noSymbolSelected,
    loadingCharts,
    chartData.length,
    mainChartType,
    handleExpandChart,
  ]);

  const onExpandRsi = useMemo(() => {
    if (noSymbolSelected || loadingCharts || rsiData.length === 0)
      return undefined;
    return () => handleExpandChart("Rsi", "RSI Trend");
  }, [noSymbolSelected, loadingCharts, rsiData.length, handleExpandChart]);

  const hpbTitle = useMemo(
    () =>
      `Predicting Stock Movement over each month from HpB${hpbYear ? " since " + hpbYear : ""}`,
    [hpbYear],
  );

  const onExpandHpb = useMemo(() => {
    if (noSymbolSelected || loadingCharts || hpbData.length === 0)
      return undefined;
    return () => handleExpandChart("Hpb", hpbTitle);
  }, [
    noSymbolSelected,
    loadingCharts,
    hpbData.length,
    handleExpandChart,
    hpbTitle,
  ]);

  const mainChartTitle = (
    <div className="flex min-w-0 flex-col md:flex-row md:items-center gap-1.5 md:gap-3">
      <span className="shrink-0 whitespace-nowrap">
        {noSymbolSelected
          ? "1 Year Chart (TF: 1 Day)"
          : `${symbol}: 1 Year Chart (TF: 1 Day)`}
      </span>
      {!noSymbolSelected &&
        !loadingCharts &&
        !toggleChart &&
        chartData.length > 0 && (
          <div
            className={`${candleDetailsCardWrapperClass} max-[1750px]:hidden`}
            title={getCandleDetailsTitle(activeCandle)}
          >
            {renderCandleDetailsReact(activeCandle)}
          </div>
        )}
    </div>
  );

  const rsiChartTitle = noSymbolSelected ? "RSI Trend" : `${symbol}: RSI Trend`;

  const hpbChartTitle = noSymbolSelected ? hpbTitle : `${symbol}: ${hpbTitle}`;

  const expandedModalTitle = `
    <div class="flex flex-col md:flex-row md:items-center gap-1.5 md:gap-4">
      <span class="whitespace-nowrap">${symbol}: ${expandChartTitle}</span>
      ${!chartData || chartData.length === 0 || expandChart !== "Candle" ? "" : renderCandleDetailsHtml(activeCandle)} 
    </div>
  `;

  const expandedChartContent = useMemo(
    () => (showModal ? renderChart(expandChart, true) : null),
    [showModal, expandChart, renderChart],
  );

  return (
    <>
      <div className="grid grid-cols-1 grid-rows-3 gap-1 mb-[4px] h-full">
        <ChartCard
          title={mainChartTitle}
          ontoggle={!noSymbolSelected}
          toggleChart={toggleChart}
          setToggleChart={setToggleChart}
          onExpand={onExpandMain}
          isOrderblock={true}
          compactHeader={true}
        >
          {noSymbolSelected ? (
            <PlaceholderMessage message="Please select a symbol to view chart" />
          ) : loadingCharts ? (
            <ChartSkeleton isOrderBlock={true} />
          ) : chartData.length === 0 ? (
            <PlaceholderMessage message="No data found" />
          ) : (
            renderChart(mainChartType)
          )}
        </ChartCard>
        <ChartCard
          title={rsiChartTitle}
          onExpand={onExpandRsi}
          isOrderblock={true}
          compactHeader={true}
        >
          {noSymbolSelected ? (
            <PlaceholderMessage message="Please select a symbol to view RSI trend" />
          ) : loadingCharts ? (
            <ChartSkeleton isOrderBlock={true} />
          ) : rsiData.length === 0 ? (
            <PlaceholderMessage message="No data found" />
          ) : (
            renderChart("Rsi")
          )}
        </ChartCard>
        <ChartCard
          title={hpbChartTitle}
          onExpand={onExpandHpb}
          isOrderblock={true}
          compactHeader={true}
        >
          {noSymbolSelected ? (
            <PlaceholderMessage message="Please select a symbol to view prediction" />
          ) : loadingCharts ? (
            <ChartSkeleton isOrderBlock={true} />
          ) : hpbData.length === 0 ? (
            <PlaceholderMessage message="No data found" />
          ) : (
            renderChart("Hpb")
          )}
        </ChartCard>
      </div>
      {showModal && (
        <Suspense fallback={<ChartSkeleton isOrderBlock={true} />}>
          <DialogModal
            isChart={true}
            title={expandedModalTitle}
            message={expandedChartContent}
            show={showModal}
            setShow={setShowModal}
          />
        </Suspense>
      )}
    </>
  );
});

export default OrderBlockChart;
